import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import kpLogo from '@/assets/kp-logo.png';
import { requestOtp, verifyOtp } from '@pams/client-core/auth';
import { OTP_CODE_LENGTH, maskPhone, requestOtpSchema, verifyOtpSchema, type OtpChallengeDto } from '@pams/shared';
import { useAuth } from '@/hooks/useAuth';
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import { useZodForm } from '@/lib/zodForm';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE FIRST THING ANYONE SEES.
 *
 *  A single considered column on the CANVAS colour, with no card. In light mode
 *  `background` and `card` are both white, so the card P1a shipped was an invisible box
 *  that only appeared in dark mode — the "considered single-column layout on the canvas
 *  colour" the brief asks for is what the tokens were already implying.
 *
 *  ⚠️ ONE "Phone or email" FIELD. NOT A TOGGLE.
 *  FR-5.8.4: "Login accepts either identifier on the dashboard, resolved through one
 *  identifier-agnostic lookup… The UI is a single 'Phone or email' field, not a toggle."
 *  A toggle would put the type decision on the user, which means two code paths on the
 *  server to serve it.
 *
 *  ⚠️ NO RADIO, NO TAB, NO COMBOBOX may appear on this screen — `App.test.tsx` asserts
 *  all three are absent. That rules out AntD's `Segmented` (it renders `<input
 *  type="radio">`), a `Tabs` step indicator, and a country-code `Select` on the field.
 *  The second step is therefore communicated as TEXT, not as a stepper.
 *
 *  ⚠️ THE BRAND MARK IS A SIBLING OF THE HEADING, never inside it. `<h3><img/>PA
 *  Admin — Sign in to PA Admin</h3>` computes an accessible name of "PA Admin Sign in
 *  to PA Admin" and fails five assertions at once.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
/*
 * [FIX-2] The digit count is gone from the HELP. A rule stated before anybody has typed
 * anything is noise the operator has to carry; the same sentence arrives as an error, on
 * submit, at the moment it is actually useful.
 */
const IDENTIFIER_HELP = 'Your mobile number, or the email address registered with your unit.';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, signedOutReason, clearSignedOutReason } = useAuth();
  const [challenge, setChallenge] = useState<OtpChallengeDto | undefined>();
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState<string | undefined>();

  /**
   * ⚠️ [LIFECYCLE] THE ATTEMPT COUNTER EXISTS ONLY TO REMOUNT `Input.OTP`.
   *
   * Clearing the field is not enough on its own: after a failed verify the caret is left
   * wherever the sixth digit was, so an operator retyping has to click back to box one.
   * `key={attempt}` remounts the INPUT — not the `Form.Item` — so the field stays
   * registered (CLAUDE.md §8's unmount rule is untouched) and `autoFocus` fires again.
   *
   * A page-local counter rather than `verifyOtpMutation.failureCount`, whose reset semantics are
   * library behaviour this page would then depend on.
   */
  const [attempt, setAttempt] = useState(0);

  const identifierForm = useZodForm(requestOtpSchema);
  const codeForm = useZodForm(verifyOtpSchema);

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ THE OTP BOX OUTLIVED EVERY INTERACTION THAT SHOULD HAVE EMPTIED IT.
   *
   *  `Form.useForm()` is called HERE, in the page, not in the step-2 subtree — and
   *  rc-field-form's `preserve` defaults to `true`. So the store outlives the `<Form>` that
   *  displayed it: leaving step 2 and coming back re-registered `code` and repopulated
   *  `Input.OTP` with the digits that had already failed.
   *
   *  The visible cost was an operator retrying the SAME wrong code and blaming the system,
   *  because the box looked like a fresh one.
   *
   *  ⚠️ `resetFields()`, NOT `setFieldsValue({ code: '' })`. The second empties the value and
   *  leaves the field's ERROR and touched state behind, so the box comes back blank and
   *  still red — which reads as "this empty field is invalid".
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const clearCode = useCallback(() => {
    /*
     * ⚠️ `setFields` WITH AN EXPLICIT EMPTY STRING, NOT `resetFields()`. MEASURED: both
     * empty the store — `getFieldsValue(true)` returns `{}` either way — but `resetFields`
     * leaves the field UNDEFINED, and `Form.Item` then injects its cached value into the
     * freshly-mounted `Input.OTP`, so all six boxes came back holding the code that had
     * just failed. An empty STRING is a value the control honours.
     *
     * It also clears `errors` and `touched` in the same call, which `setFieldsValue` alone
     * would not: an empty box still wearing a red border reads as "this blank field is
     * invalid", which is the visible half of the same bug.
     */
    codeForm.form.setFields([{ name: 'code', value: '', errors: [], touched: false }]);
    setAttempt((previous) => previous + 1);
  }, [codeForm.form]);

  /**
   * ⚠️ CALLED AT THE TWO WRITERS OF `identifier`, NOT FROM AN EFFECT KEYED ON IT.
   *
   * An effect was the first shape — one key, one reason, covering any future writer — and
   * `eslint-plugin-react-hooks` refuses it: calling `setState` synchronously in an effect
   * body is a cascading render, and the rule is right about the cause. Clearing the box is a
   * consequence of an INTERACTION (a new number was submitted, or the operator went back),
   * never a synchronisation with anything outside React.
   *
   * The cost is that a third writer of `identifier` would have to remember. There are two,
   * both immediately below, and this comment is the marker for anyone adding a third.
   */

  /**
   * ⚠️ [P4-UX.1] THE USEFUL SENTENCE IS IN `details`, NOT IN `message`.
   *
   * A `ValidationError` envelope is `{ message: 'Request validation failed', details: [{
   * path: 'body.identifier', message: 'That reduces to 9 digits…' }] }`. Rendering
   * `cause.message` — which this page did — shows the operator the generic half and throws
   * away the half written for them.
   *
   * Most of these never reach the client now that `identifierSchema` validates properly,
   * but the server stays the authority: anything it rejects that the form did not is
   * exactly the case where a clear sentence matters most.
   */
  const describe = (cause: ApiError): string => {
    const issues: unknown[] = Array.isArray(cause.details) ? cause.details : [];
    const first = issues.find(
      (issue): issue is { message: string } =>
        typeof issue === 'object' && issue !== null && typeof (issue as { message?: unknown }).message === 'string',
    );
    return first?.message ?? cause.message;
  };

  const requestOtpMutation = useMutation({
    mutationFn: (value: string) => requestOtp(api, { identifier: value, surface: 'dashboard', origin: 'admin' }),
    onSuccess: (data) => {
      setError(undefined);
      setChallenge(data);
    },
    onError: (cause: ApiError) => setError(describe(cause)),
  });

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [OTP-KP] THE RESEND COOLDOWN, TICKED LOCALLY SO THE CONTROL CAN SAY WHY IT IS OFF.
   *
   *  `resendAfter` has been on the challenge since P1a and nothing has ever rendered it — so
   *  the server has carried a cooldown an operator could only discover by pressing a button
   *  and being told "Too many requests". The countdown is the same fact, before the click.
   *
   *  ⚠️ THE TIMER STOPS ITSELF. A bare `setInterval` would re-render this screen once a second
   *  for as long as the code step is open; it is cleared the moment the remainder is zero.
   *
   *  ⚠️ AND IT IS AN OFFER GATE, NEVER THE CONTROL. `consumeRateSlot` enforces the cooldown
   *  server-side and no admin role can clear it (FR-5.8.17); this reads a DEVICE clock, which
   *  an operator can set to next year.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const [now, setNow] = useState(() => Date.now());
  const resendInSeconds =
    challenge === undefined ? 0 : Math.max(0, Math.ceil((Date.parse(challenge.resendAfter) - now) / 1_000));

  useEffect(() => {
    if (resendInSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [resendInSeconds]);

  const verifyOtpMutation = useMutation({
    /*
     * ⚠️ THE WHOLE VALIDATED PAYLOAD, NOT JUST THE CODE. `codeForm.submit` has already
     * parsed `{requestId, code}` through `verifyOtpSchema`, so passing `parsed.data`
     * straight through is what makes `requestId` a `string` here rather than the
     * `challenge?.requestId` this line used to read — which was optional at the type level
     * on a step that cannot be reached without a challenge. Same request body either way.
     */
    mutationFn: (input: { requestId: string; code: string }) => verifyOtp(api, input),
    onSuccess: (session) => {
      signIn(session);
      void navigate('/', { replace: true });
    },
    onError: (cause: ApiError) => {
      setError(describe(cause));
      // A stale code in the box is how somebody retries the same wrong code.
      clearCode();
    },
  });

  /** The step, as a sentence rather than as a control. */
  const subtitle = challenge
    ? `Enter the ${OTP_CODE_LENGTH}-digit code sent to ${/@/.test(identifier) ? identifier : maskPhone(identifier)}.`
    : IDENTIFIER_HELP;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm">
        {/*
          Brand mark: a SIBLING of the heading. The image is decorative — the wordmark beside
          it carries the name, and an `alt` here would say it twice.

          ⚠️ IT IS GENERATED FROM `docs/design/kp-logo.png`, not hand-placed. It was `Building2`
          from lucide, a placeholder that outlived its excuse.
        */}
        <div className="mb-6 flex items-center gap-2 text-muted-foreground">
          <img src={kpLogo} alt="" aria-hidden className="size-5" width={20} height={20} />
          <span className="text-sm font-semibold tracking-tight">PA Admin</span>
        </div>

        <Typography.Title level={3} className="mb-1">
          Sign in to PA Admin
        </Typography.Title>
        <p className="mb-6 text-sm text-muted-foreground">{subtitle}</p>

        {/*
         * ⚠️ [C1a] A SEPARATE ALERT FROM `login-error`, AND THAT IS THE POINT.
         *
         * "Your session ended" and "that code is wrong" are two different facts about two
         * different moments. Sharing one slot would mean the first wrong code silently
         * erased the only explanation the operator was ever given for being here.
         */}
        {signedOutReason && (
          <Alert type="warning" showIcon className="mb-4" message={signedOutReason} data-testid="signed-out-reason" />
        )}
        {error && <Alert type="error" showIcon className="mb-4" message={error} data-testid="login-error" />}

        {!challenge ? (
          <Form
            form={identifierForm.form}
            layout="vertical"
            initialValues={{ surface: 'dashboard' }}
            onFinish={(values) => {
              // Starting over answers the question the notice was answering.
              clearSignedOutReason?.();
              const parsed = identifierForm.submit({ ...values, surface: 'dashboard' });
              if (parsed.ok) {
                setIdentifier(parsed.data.identifier);
                // ⚠️ Writer 1 of `identifier`: a NEW number must not inherit the digits
                // typed against the old one.
                clearCode();
                requestOtpMutation.mutate(parsed.data.identifier);
              }
            }}
          >
            {/* Rules come from the shared schema — never hand-typed (PAMS_CONVENTIONS §6). */}
            <Form.Item
              {...identifierForm.field('identifier')}
              label="Phone or email"
              /*
            ⚠️ [FIX-2] VALIDATE ON SUBMIT ONLY, AND `validateTrigger={[]}` IS WHAT DOES IT.
            AntD's default is `onChange`, so a number went red at the FIRST keystroke and
            stayed red the whole way to the tenth — telling an operator they are wrong while
            they are still typing. An empty trigger list removes every automatic trigger;
            `form.validateFields()`, which `onFinish` runs, is unaffected, so the rule still
            fires on submit and the error still arrives before any request is sent.
          */
              validateTrigger={[]}
            >
              <Input
                autoFocus
                autoComplete="username"
                placeholder="9876543210 or you@example.gov.in"
                data-testid="identifier-input"
              />
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={requestOtpMutation.isPending}>
              Send code
            </Button>
          </Form>
        ) : (
          /*
           * ⚠️ THE `key` IS ON THE FORM, AND IT HAD TO BE. It was on `Input.OTP`, which does
           * nothing: `Form.Item` CLONES its child, and the clone does not carry the key
           * through to React's reconciler — so the element re-rendered with fresh props
           * (the `data-testid` even changed) while `Input.OTP` kept the six digits it had
           * mounted with. MEASURED: the store was empty and the boxes were not.
           *
           * Remounting the whole step is safe HERE and would not be on the builders:
           * `preserve` keeps the store, this form holds exactly one field, and nothing on
           * this screen derives anything from `getFieldsValue()` — which is the property
           * CLAUDE.md §8's do-not-unmount rule actually protects.
           */
          <Form
            key={attempt}
            form={codeForm.form}
            layout="vertical"
            onFinish={(values) => {
              const parsed = codeForm.submit({ ...values, requestId: challenge.requestId });
              if (parsed.ok) verifyOtpMutation.mutate(parsed.data);
            }}
          >
            <Form.Item {...codeForm.field('code')} label={`${OTP_CODE_LENGTH}-digit code`}>
              <Input.OTP length={OTP_CODE_LENGTH} autoFocus inputMode="numeric" data-testid="otp-input" />
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={verifyOtpMutation.isPending}>
              Sign in
            </Button>
            {/*
             * ⚠️ [OTP-KP] RESEND RE-DELIVERS THE SAME CODE, THREE TIMES, AND THEN A NEW ONE —
             * the server decides which, and this screen must not try to. It calls
             * `request-otp` again and REPLACES `challenge` with whatever comes back, so the
             * `requestId` it verifies against is always the latest. Holding the old id after a
             * resend that superseded it is the one way this control can break, and reassigning
             * is what makes that unrepresentable rather than remembered.
             *
             * ⚠️ IT DOES NOT REMOUNT THE STEP. `key={attempt}` is what clears `Input.OTP`'s
             * buffered digits (see the comment above it), and a resend must NOT clear them: an
             * officer who pressed Resend because the first text was slow may well be typing the
             * first code when the second arrives, and both are valid — it is the same code.
             */}
            <Button
              type="link"
              block
              className="mt-2"
              disabled={resendInSeconds > 0 || requestOtpMutation.isPending}
              onClick={() => {
                setError(undefined);
                requestOtpMutation.mutate(identifier);
              }}
            >
              {resendInSeconds > 0 ? `Resend code in ${String(resendInSeconds)}s` : 'Resend code'}
            </Button>
            <Button
              type="link"
              block
              onClick={() => {
                setChallenge(undefined);
                setError(undefined);
                /*
                 * ⚠️ WRITER 2 OF `identifier` — AND ITS FORM TOO. Clearing the state without
                 * clearing the FIELD meant an operator who came back to change their number
                 * typed into a box that still held the old one, producing
                 * `98000000019800000002` and a validation failure they had no way to
                 * explain. Both, or neither works.
                 */
                setIdentifier('');
                identifierForm.form.resetFields();
                clearCode();
              }}
            >
              Use a different phone or email
            </Button>
          </Form>
        )}

        {/*
          The bypass warning as a DELIBERATE element of the composition — inside the column,
          sharing its measure, below the action — rather than a full-bleed alert bolted to
          the top of an otherwise empty page. Same words, same testid, still undismissable.
        */}

        {/*
         * ─────────────────────────────────────────────────────────────────────────────
         *  THE VENDOR CREDIT, ON EVERY STEP OF THE SIGN-IN.
         *
         *  ⚠️ IT SITS OUTSIDE THE `challenge` TERNARY, which is what makes "across the login
         *  pages" true: the two steps are two branches of ONE render, and putting it inside
         *  either would show it on one of them and not the other.
         *
         *  ⚠️ `rel="noopener noreferrer"` IS NOT OPTIONAL ON `target="_blank"`. Without
         *  `noopener` the opened page gets a `window.opener` handle back into this one and can
         *  navigate it — a credential-phishing primitive on the one page in this product whose
         *  whole subject is signing in. Browsers imply it for `_blank` now; the attribute is
         *  what makes it true of any that do not.
         *
         *  ⚠️ AND IT IS ONE SENTENCE WITH A LINK INSIDE IT, not a link with words around it.
         *  Only the company is clickable, so that IS the link's accessible name.
         */}
        <p className="mt-10 text-center text-xs text-muted-foreground">
          Powered by{' '}
          <a
            href="https://mindwebs.org"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-link underline-offset-2 hover:underline"
          >
            Mind Webs Ventures
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
